import { z } from 'zod/v4';

import { formViewSchema } from '@sites/entity-core';

import { formAppearanceShape } from '../form-shared/partial-form-appearance.schema';

/**
 * THE subscription-form data shape as AI tools accept it — shared by `create_form_widget` and
 * `update_form_widget` so their field surfaces cannot drift (the pattern every other type follows).
 *
 * Almost all of it is `formAppearanceShape`, declared in `form-shared/` because the payment form
 * stores the same four groups from the same entity-core schemas. What is here is what is genuinely
 * this widget's own: the view mode and the modal heading.
 *
 * **What is deliberately absent, and why it is not an oversight.** The persisted form carries four
 * groups of external references: `selectedAddressBook`, the CRM pipeline trio, `contactTags`/
 * `contactAddressBookTags`, and `chatBots` (bot ids and chain ids). None of them are here. A form
 * bound to an address book that does not exist looks finished and loses every lead it collects, and
 * nothing on the page or in the editor says so — the failure surfaces as "the campaign didn't work".
 * They arrive once there is a tool that can LIST the account's real books, pipelines and tags (§3.6
 * stage A of `ai-mcp-plan.md`), and not before.
 */
export const partialFormWidgetDataSchema = z.object({
    ...formAppearanceShape,

    // --- view mode ---
    formView: formViewSchema
        .optional()
        .describe(
            '"static" (default) renders the form inline on the page; "modal" renders a button that ' +
                'opens the form in a dialog.'
        ),
    modalTitle: z.string().optional().describe('Heading inside the dialog — "modal" view only.'),
});

export type PartialFormWidgetData = z.infer<typeof partialFormWidgetDataSchema>;

export type { PartialFormField } from '../form-shared/partial-form-appearance.schema';

/**
 * The keys this widget keeps on `data` itself. Handed to the shared fold rather than unioned into it,
 * so a `modalTextHint` meant for a payment form cannot land on a subscription form.
 */
export const FORM_DATA_KEYS: readonly string[] = ['formView', 'modalTitle'];
